/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment } from 'react'
import { observer } from 'mobx-react'
import { Modal } from 'antd-mobile'
import { DialogType } from '@store/globalStore'
import { ModalBg } from '@styles/base.style'
import { Button } from '@styles/button.style'
import styled from '@themes/theme'
import { CommonGmoneyStyle } from '@views/Matchup/BetSlip/index.style'
import { GmoneyIco } from '@shared/Icon/MoneyIcon'
import { G_ICON } from '@game/util'
import { AdvDialogBtnWrap, AdvDialogStyle } from '@styles/modal'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'

const GmoneyStyle = styled.span`
  ${CommonGmoneyStyle};
  width: 14px;
  top: 2px;
  margin-left: 2px;
  float: none;
`
const GIcon = (
  <GmoneyStyle className="gmoney">
    <GmoneyIco/>
  </GmoneyStyle>
)

export interface DialogOptions {
  id?: string // 1개 이상의 dialog 를 다이얼로그 큐에 넣지 않게 하기 위한 구분자
  title?: string
  iconTitle?: React.ReactNode
  custom?: React.ReactNode
  text?: string[]
  className?: string
  wrapClassName?: string

  singlePress?: string

  negativePress?: string
  positivePress?: string

  callbackPositive?: () => void
  callbackNegative?: () => void

  // 확인이든 취소든 누르면 항상 창이 닫히는 이유로
  // 확인 버튼을 누른뒤 반환값에 따라 창을 닫을지 결정 한다.
  // callbackPositive 와 callbackPositiveFreeze 둘중 하나만 사용 가능하다.
  callbackPositiveFreeze?: () => boolean | Promise<boolean>

  callbackCloseDialog?: () => void
  callbackCloseDialogBg?: () => void

  topBanner?: React.ReactNode
}

export interface DialogProps extends DialogOptions {
  id: string
  type: DialogType
  callbackClose: () => void
}

const Dialog: React.FC<DialogProps> = (props: DialogProps) => {
  let key = false
  const {
    id,
    type,
    title,
    iconTitle,
    custom,
    text = [],
    callbackClose,
    callbackCloseDialog,
    callbackCloseDialogBg,
    singlePress,
    negativePress,
    positivePress,
    callbackPositive,
    callbackNegative,
    callbackPositiveFreeze,
    className,
    wrapClassName,
    topBanner,
  } = props

  let replaceTitle = null
  let customTitle = null

  if (title) {
    customTitle = title
    if (title.indexOf(G_ICON) > 0) {
      replaceTitle = title.replace(G_ICON, '')
      customTitle = (<Fragment>{replaceTitle}{GIcon}</Fragment>)
    }
  } else if (iconTitle) {
    customTitle = iconTitle
  }

  let contents
  if (custom) {
    key = true
    contents = custom
  } else {
    key = false
    contents = []
    let count = 1
    for (const el of text) {
      const brTagKey = `Dialog:Contents:Br:${count}`
      const popupTextKey = `Dialog:Contents:PopupTxt:${count}`
      if (contents.length > 0) {
        contents.push(<br key={brTagKey}/>)
      }
      if (title || iconTitle) {
        contents.push(<p key={popupTextKey} className="modal-body-txt">{el}</p>)
      } else {
        contents.push(
          <p key={popupTextKey} className="modal-body-txt no-title">
            {el}
          </p>,
        )
      }
      count += 1
    }
  }

  const onClickPopupCloseButton = (event): void => {
    event.preventDefault()
    callbackClose()
    if (callbackCloseDialog) {
      callbackCloseDialog()
    }
  }
  const onClickNegativeButton = (event): void => {
    event.preventDefault()
    callbackClose()
    if (callbackNegative) {
      callbackNegative()
    }
    if (callbackCloseDialog) {
      callbackCloseDialog()
    }
  }
  const onClickPositiveButton = (event): void => {
    event.preventDefault()
    callbackClose()
    if (callbackPositive) {
      callbackPositive()
    }
    if (callbackCloseDialog) {
      callbackCloseDialog()
    }
  }

  const onClickPositiveFreezeButton = (): boolean | Promise<boolean> => {
    if (callbackPositiveFreeze) {
      return callbackPositiveFreeze()
    }
    return false
  }

  let footer
  if (type === DialogType.SINGLE) {
    footer = (
      <AdvDialogBtnWrap className="adv-dialog-btn-wrap">
        <Button secondary onClick={onClickPopupCloseButton} className="single-btn">
          {singlePress}
        </Button>
      </AdvDialogBtnWrap>
    )
  } else if (type === DialogType.CHOICE) {
    footer = (
      <AdvDialogBtnWrap className="adv-dialog-btn-wrap">
        <Button onClick={onClickNegativeButton} className="choice-btn">
          {negativePress}
        </Button>
        {callbackPositiveFreeze && (
          <Button secondary className="choice-btn positive" onClick={async (event) => {
            event.preventDefault()
            if (await onClickPositiveFreezeButton()) {
              callbackClose()
              if (callbackCloseDialog) {
                callbackCloseDialog()
              }
            }
          }}>
            {positivePress}
          </Button>
        )}
        {!callbackPositiveFreeze && (
          <Button secondary onClick={onClickPositiveButton} className="choice-btn positive">
            {positivePress}
          </Button>
        )}
      </AdvDialogBtnWrap>
    )
  } else {
    footer = (
      <AdvDialogBtnWrap/>
    )
  }
  const modalClassName = ['adv-new-dialog']
  if (key) {
    modalClassName.push('adv-custom-dialog')
    if (!isMobile) {
      modalClassName.push('adv-custom-dialog pc')
    }
  } else {
    modalClassName.push('adv-normal-dialog')
    if (!isMobile) {
      modalClassName.push('adv-normal-dialog pc')
    }
  }

  if (className) {
    modalClassName.push(className) // test-1 test2
  }

  const detailContentStyle = classNames('adv-dialog-detail-content', {
    custom
  })

  return (
    <Modal
      key={id}
      visible
      transparent
      maskClosable={false}
      onClose={callbackClose}
      title={customTitle}
      wrapClassName={wrapClassName}
      className={modalClassName.join(' ')}
      css={AdvDialogStyle}
    >
      {topBanner && <aside className="dialog-top-banner">{topBanner}</aside>}
      <div className={detailContentStyle}>
        {contents}
      </div>
      {footer}
      <ModalBg className="dialog-bg" onClick={callbackCloseDialogBg}/>
    </Modal>
  )
}

export default observer(Dialog)
